import { Helmet } from "react-helmet-async";

const SITE_URL = "https://monitoring.nationalbusinessregister.co.uk";
const SITE_NAME = "NBR Trade Mark Monitoring";
const DEFAULT_IMAGE = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

export const SEOHead = ({ 
  title, 
  description, 
  path = "/", 
  type = "website",
  schema = null,
  noindex = false
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - UK Trade Mark Protection Service`;
  const canonicalUrl = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />

      {/* Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

// Schema.org structured data generators
export const organisationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "National Business Register",
  "alternateName": "NBR",
  "url": "https://nationalbusinessregister.co.uk",
  "logo": DEFAULT_IMAGE,
  "foundingDate": "1984",
  "description": "The UK's leading intellectual property experts, providing trade mark registration, monitoring, and protection services since 1984.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Suite 7, The Courtyard, Russell House, 6 Doctors Lane",
    "addressLocality": "Henley-in-Arden",
    "addressRegion": "Warwickshire",
    "postalCode": "B95 5AW",
    "addressCountry": "GB"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+44-800-069-9090",
    "contactType": "customer service",
    "areaServed": "GB",
    "availableLanguage": "English"
  },
  "sameAs": [
    "https://nationalbusinessregister.co.uk"
  ]
};

export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "NBR Trade Mark Monitoring",
  "description": "Post-registration trade mark monitoring service that scans newly filed UK trade mark applications for similarity against your registered marks, identifying potentially conflicting filings early while opposition options remain available.",
  "provider": {
    "@type": "Organization",
    "name": "National Business Register",
    "url": "https://nationalbusinessregister.co.uk"
  },
  "serviceType": "Trade Mark Monitoring",
  "areaServed": {
    "@type": "Country",
    "name": "United Kingdom"
  },
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "GBP",
    "lowPrice": "14",
    "highPrice": "20",
    "offerCount": "4",
    "priceSpecification": {
      "@type": "UnitPriceSpecification",
      "price": "14",
      "priceCurrency": "GBP",
      "unitText": "per trade mark per month",
      "description": "Volume-based pricing from £14 to £20 per trade mark per month"
    }
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Trade Mark Monitoring Plans",
    "itemListElement": [
      {
        "@type": "Offer",
        "name": "1 Trade Mark",
        "price": "20",
        "priceCurrency": "GBP",
        "description": "Standard rate for single trade mark monitoring"
      },
      {
        "@type": "Offer",
        "name": "2-5 Trade Marks",
        "price": "18",
        "priceCurrency": "GBP",
        "description": "10% volume discount"
      },
      {
        "@type": "Offer",
        "name": "6-10 Trade Marks",
        "price": "16",
        "priceCurrency": "GBP",
        "description": "20% volume discount"
      },
      {
        "@type": "Offer",
        "name": "11+ Trade Marks",
        "price": "14",
        "priceCurrency": "GBP",
        "description": "30% volume discount"
      }
    ]
  }
};

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.a
    }
  }))
});

export const generateBlogSchema = (post, url) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": post.title,
  "description": post.excerpt,
  "datePublished": post.date,
  "dateModified": post.date,
  "author": {
    "@type": "Organization",
    "name": "National Business Register"
  },
  "publisher": {
    "@type": "Organization",
    "name": "National Business Register",
    "logo": {
      "@type": "ImageObject",
      "url": DEFAULT_IMAGE
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": url
  },
  "image": post.image_url || DEFAULT_IMAGE
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": item.name,
    "item": item.url ? `https://monitoring.nationalbusinessregister.co.uk${item.url}` : undefined
  }))
});
